import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { CalendarView } from './types';
import { DAYS_FR, MONTHS_FR_SHORT, getCalendarDays, isSameDay, isInRange, isDateDisabled } from './utils';

interface CalendarGridProps {
  year: number;
  month: number;
  showNavLeft: boolean;
  showNavRight: boolean;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
  highlightStart: Date | null;
  highlightEnd: Date | null;
  /** Jour civil "aujourd'hui" a surligner (champs locaux). */
  today: Date;
  minDate?: Date;
  maxDate?: Date;
  asSingle: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onDayClick: (date: Date) => void;
  onDayHover: (date: Date | null) => void;
  onViewChange: (view: CalendarView, side: 'left' | 'right') => void;
  side: 'left' | 'right';
}

export function CalendarGrid({
  year,
  month,
  showNavLeft,
  showNavRight,
  isPrevDisabled,
  isNextDisabled,
  highlightStart,
  highlightEnd,
  today,
  minDate,
  maxDate,
  asSingle,
  onPrevMonth,
  onNextMonth,
  onDayClick,
  onDayHover,
  onViewChange,
  side,
}: CalendarGridProps) {
  const days = getCalendarDays(year, month);

  return (
    <div className="w-[224px]">
      {/* Month Header with Navigation */}
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={onPrevMonth}
          disabled={isPrevDisabled}
          className={cn(
            'rounded p-1 text-neutral-400 transition-colors',
            'hover:bg-neutral-100 hover:text-neutral-600',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            (!showNavLeft || isPrevDisabled) && 'invisible'
          )}
          aria-label="Mois précédent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewChange('months', side)}
            className="text-sm font-semibold text-primary-500 hover:text-primary-700 transition-colors"
          >
            {MONTHS_FR_SHORT[month]}
          </button>
          <button
            onClick={() => onViewChange('years', side)}
            className="text-sm font-semibold text-dark-500 hover:text-primary-500 transition-colors"
          >
            {year}
          </button>
        </div>

        <button
          onClick={onNextMonth}
          disabled={isNextDisabled}
          className={cn(
            'rounded p-1 text-neutral-400 transition-colors',
            'hover:bg-neutral-100 hover:text-neutral-600',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            (!showNavRight || isNextDisabled) && 'invisible'
          )}
          aria-label="Mois suivant"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Days Header */}
      <div className="mb-1 grid grid-cols-7">
        {DAYS_FR.map((day) => (
          <div
            key={day}
            className="flex h-8 w-8 items-center justify-center text-xs font-medium text-neutral-400"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7">
        {days.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="h-8 w-8" />;
          }

          const isCurrentMonth = date.getMonth() === month;
          const dayDisabled = isDateDisabled(date, minDate, maxDate);
          const isStart = isSameDay(date, highlightStart);
          const isEnd = isSameDay(date, highlightEnd);
          const inRange = isInRange(date, highlightStart, highlightEnd);
          const isToday = isSameDay(date, today);
          const isSelected = isStart || isEnd;

          return (
            <button
              key={`${date.toISOString()}-${index}`}
              onClick={() => onDayClick(date)}
              onMouseEnter={() => !asSingle && onDayHover(date)}
              onMouseLeave={() => !asSingle && onDayHover(null)}
              disabled={dayDisabled}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded text-sm transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                dayDisabled && 'text-neutral-200 cursor-not-allowed pointer-events-none',
                !dayDisabled && !isCurrentMonth && 'text-neutral-300',
                !dayDisabled && isCurrentMonth && !isSelected && !inRange && 'text-dark-500 hover:bg-neutral-100',
                !dayDisabled && inRange && !isSelected && 'bg-primary-50 text-primary-600',
                !dayDisabled && isSelected && 'bg-primary-500 font-semibold text-white hover:bg-primary-600',
                !dayDisabled && isToday && !isSelected && 'bg-primary-100 font-bold'
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
